/**
 * ⓘ Como ler esta página — o mesmo botão nas duas experiências do Portal.
 *
 * ## Por que uma tela externa precisa disto
 *
 * Quem abre este portal não trabalha aqui. As palavras que a agência usa todo dia
 * ("liberada", "quitada", "a faturar") são precisas para nós e opacas para quem está do
 * outro lado, e o preço de não explicá-las é um telefonema por pedido. O ⓘ põe a
 * explicação a um clique, sem empurrar prosa para dentro da tela de quem já entendeu.
 *
 * ## O modal reaproveita a moldura que já existe
 *
 * `.veu` e `.modal` já vestem o extrato nesta folha. Um segundo desenho de modal na
 * mesma casca seriam duas linguagens para a mesma coisa — e o portal tem folha própria,
 * então não há de onde importar uma terceira.
 */
import { useState } from 'react'
import { ModalDoPortal } from './modal.js'
import { Info, X } from 'lucide-react'

export function ComoLerEstaPagina({ parceiro, variasEmpresas }: {
  parceiro: boolean
  /** O portal reúne mais de uma empresa: a ajuda explica "Todas" e o seletor (D-131). */
  variasEmpresas: boolean
}) {
  const [aberto, setAberto] = useState(false)

  return (
    <>
      <button
        type="button"
        className="botao-de-info"
        aria-label="Como ler esta página"
        title="Como ler esta página"
        onClick={() => {
          setAberto(true)
        }}
        data-teste="como-ler"
      >
        <Info size={16} />
      </button>

      {aberto && (
        <ModalDoPortal rotulo="Como ler esta página" aoFechar={() => { setAberto(false) }} teste="modal-como-ler">
            {/* A cabeça do `Modal` do app interno (sobreposicao.tsx:279-298): título sem eyebrow e o X redondo (D-134 · DS-531). */}
            <div className="modal-cabeca">
              <h2>Como ler esta página</h2>
              <button
                type="button"
                className="modal-fechar"
                aria-label="Fechar"
                onClick={() => {
                  setAberto(false)
                }}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <div className="como-ler">
              {parceiro ? <ParaOParceiro variasEmpresas={variasEmpresas} /> : <ParaOCliente variasEmpresas={variasEmpresas} />}
            </div>
        </ModalDoPortal>
      )}
    </>
  )
}

/**
 * O alcance do acesso é o PORTAL (D-131): as empresas que a DigitalView reuniu nele, e nenhuma
 * outra. A frase antiga ("cada empresa vê só os próprios dados") contradizia a visão "Todas".
 */
function OAlcanceDoPortal({ variasEmpresas }: { variasEmpresas: boolean }) {
  return (
    <>
      <li>
        O acesso é pelo portal: você vê só os dados das empresas que a DigitalView incluiu nele, e
        nada de outra empresa aparece aqui, mesmo do mesmo grupo.
      </li>
      {variasEmpresas && (
        <li>
          Em Todas as empresas, a tela soma as empresas do portal, e o que alcança mais de uma delas
          aparece uma vez só. Para ver uma empresa sozinha, escolha-a no seletor Empresa.
        </li>
      )}
    </>
  )
}

/**
 * Os resultados da campanha no cartão (§5.8; DS-677 (i)): o que são, e os três estados que a tela escreve. O mesmo
 * texto nas duas visões, porque o conteúdo publicado é um só para o Cliente, o Anunciante e o Parceiro.
 */
function OsResultadosDaCampanha({ complemento }: { complemento: string }) {
  return (
    <li>
      {complemento} impressões, cliques e CTR (os cliques sobre as impressões) da mídia programática das Campaigns
      vinculadas, com o período dos resultados e a hora da última atualização. Não incluem outras mídias.
    </li>
  )
}

function AsSituacoesDosDados() {
  return (
    <>
      <li>Completo até dd/mm: os resultados cobrem todas as Campaigns vinculadas até essa data.</li>
      <li>
        Em atualização: a DigitalView está atualizando os resultados, e o cartão fica sem números até a próxima
        publicação.
      </li>
      <li>Ainda sem dados: a campanha ainda não tem resultados de mídia programática para mostrar.</li>
    </>
  )
}

/** O selo da campanha (P-11, D-189): o que a DigitalView registrou. A mesma legenda no Cliente e no bloco do Parceiro. */
function AsSituacoesDaCampanha() {
  return (
    <>
      <li>
        <span className="badge verde">Em andamento</span> a campanha está no ar.
      </li>
      <li>
        <span className="badge slate">Ainda não começou</span> a campanha ainda vai começar.
      </li>
      <li>
        <span className="badge slate">Pausada pela DigitalView</span> a DigitalView registrou a pausa da campanha.
      </li>
      <li>
        <span className="badge slate">Concluída pela DigitalView</span> a DigitalView registrou o fim da veiculação.
      </li>
    </>
  )
}

function ParaOParceiro({ variasEmpresas }: { variasEmpresas: boolean }) {
  return (
    <>
      <section>
        <p className="eyebrow">O QUE ESTA TELA MOSTRA</p>
        <ul>
          <li>
            Suas comissões, uma linha por parcela, com a base de cálculo, o percentual, o valor e
            quando cada uma libera.
          </li>
          <li>Os negócios em que você participa e o estágio de cada um.</li>
          <li>
            As campanhas dos pedidos em que você participa, quando a DigitalView publica os resultados para você: o
            período planejado e a situação de cada uma, sem o valor da campanha.
          </li>
          <OsResultadosDaCampanha complemento="Os resultados de cada campanha publicada:" />
          <li>
            Sua trajetória: as vendas que você originou e a comissão apurada sobre elas, mês a mês.
          </li>
          <li>Os documentos que a DigitalView compartilhou com você. As notas fiscais emitidas contra a sua empresa ficam no Portal do Cliente, não aqui.</li>
          <OAlcanceDoPortal variasEmpresas={variasEmpresas} />
        </ul>
      </section>
      <section>
        <p className="eyebrow">LEGENDA</p>
        <ul className="legenda">
          <li>
            <span className="badge navy">Liberada</span> o cliente já pagou, e esta parte da sua
            comissão está liberada.
          </li>
          <li>
            <span className="badge ambar">Aguardando cliente</span> a nota já saiu para o cliente,
            falta o pagamento entrar.
          </li>
          <li>
            <span className="badge slate">A faturar</span> a nota ao cliente ainda vai ser emitida.
          </li>
          <li>
            <span className="badge slate">Prevista</span> o pedido ainda não chegou ao ponto de
            faturar: a data combinada para faturar a parcela ainda não chegou, ou a nota ao cliente
            ainda não foi autorizada. Enquanto a nota não sai, o valor ainda pode mudar.
          </li>
          <li>
            <span className="badge verde">Paga</span> você já recebeu.
          </li>
          <li>
            Comissão que você ainda não recebeu aparece sempre, em qualquer período. O período
            governa só o histórico das pagas.
          </li>
          <AsSituacoesDaCampanha />
          <AsSituacoesDosDados />
        </ul>
      </section>
      <section>
        <p className="eyebrow">TERMOS</p>
        <dl>
          <div>
            <dt>Base × %</dt>
            <dd>
              O valor sobre o qual a sua comissão incide e o percentual acordado para aquele pedido.
            </dd>
          </div>
          <div>
            <dt>Liberação na quitação</dt>
            <dd>
              Cada parcela que o cliente paga à DigitalView tem a sua parte da comissão, que libera
              inteira quando aquela parcela fica quitada. Um pagamento parcial do cliente não libera
              nada ainda.
            </dd>
          </div>
          <div>
            <dt>Previsão</dt>
            <dd>
              A data prevista para você receber: o vencimento combinado, calculado a partir do
              vencimento da parcela do cliente e do prazo de pagamento acordado com você; quando a
              parcela quita, o prazo passa a contar do dia da quitação. Enquanto a comissão não é liberada, a data vem com "prev.", porque depende do
              pagamento do cliente; sem vencimento combinado, a coluna mostra o vencimento da nota ao
              cliente.
            </dd>
          </div>
          <div>
            <dt>Extrato para faturamento</dt>
            <dd>A lista do que já está liberado, com o total e contra quem emitir a sua nota.</dd>
          </div>
          <div>
            <dt>Pipeline ativo</dt>
            <dd>
              A soma do valor dos negócios abertos em que você participa: é o valor da venda, não a sua
              comissão.
            </dd>
          </div>
        </dl>
      </section>
    </>
  )
}

function ParaOCliente({ variasEmpresas }: { variasEmpresas: boolean }) {
  return (
    <>
      <section>
        <p className="eyebrow">O QUE ESTA TELA MOSTRA</p>
        <ul>
          <li>As campanhas da sua empresa, com período, situação e investimento.</li>
          <OsResultadosDaCampanha complemento="Os resultados da campanha, quando a DigitalView os publica para a sua empresa:" />
          <li>
            Sua posição fiscal: os pedidos e as notas emitidas contra a sua empresa, com vencimento e
            pagamento.
          </li>
          <li>
            Os documentos que a DigitalView compartilhou com a sua empresa: pedidos de inserção, notas
            fiscais e relatórios de campanha. Outros arquivos ficam na aba Todos.
          </li>
          <li>
            Em Campanhas entram as campanhas em que a sua empresa é Cliente ou Anunciante; a marcada
            "Contratada por outra empresa" foi faturada a uma empresa de fora deste portal. Em Pedidos e
            notas fiscais entram só os pedidos faturados contra a sua empresa.
          </li>
          <OAlcanceDoPortal variasEmpresas={variasEmpresas} />
        </ul>
      </section>
      <section>
        <p className="eyebrow">LEGENDA</p>
        <ul className="legenda">
          <AsSituacoesDaCampanha />
          <AsSituacoesDosDados />
          <li>
            <span className="badge ambar">A faturar</span> ainda há parte do pedido sem nota, e esse
            saldo ainda não foi cobrado.
            A Situação do Pedido é comercial; iniciar, pausar ou concluir uma Campanha não a altera.
          </li>
          <li>
            <span className="badge navy">NF em emissão</span> a nota está sendo emitida.
          </li>
          <li>
            <span className="badge slate">Faturado</span> a nota foi emitida e está dentro do prazo.
          </li>
          <li>
            <span className="badge verde">Pago</span> a nota foi paga.
          </li>
          <li>
            <span className="badge vermelho">Vencido</span> há nota vencida e ainda em aberto.
          </li>
          <li>
            O período filtra as campanhas pela janela de veiculação: a campanha aparece quando a
            veiculação cruza o intervalo escolhido.
          </li>
          <li>
            Documentos e posição fiscal não respondem ao período: nota em aberto continua em aberto
            em qualquer recorte.
          </li>
        </ul>
      </section>
      <section>
        <p className="eyebrow">TERMOS</p>
        <dl>
          <div>
            <dt>PI (pedido de inserção)</dt>
            <dd>
              O documento que formaliza a compra de mídia. Aparece aqui quando a DigitalView compartilha
              o arquivo do pedido com a sua empresa.
            </dd>
          </div>
          <div>
            <dt>Em aberto</dt>
            <dd>
              O que ainda não foi pago das notas emitidas contra a sua empresa. Pedido sem nota não entra:
              o que está "A faturar" ainda não foi cobrado.
            </dd>
          </div>
        </dl>
      </section>
    </>
  )
}
